import FormDialog from '@/components/ui/form-dialog';
import RemovableTextField from '@/components/ui/removable-text-field';
import { Level } from '@/core/entities';
import useToastMessage from '@/shared/context/toast-message-context';
import { AddRounded } from '@mui/icons-material';
import { Button, Stack, capitalize } from '@mui/material';
import { ChangeEvent, FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { LOCATIONS_FORM_KEY } from '@/shared/constants/form-keys';
import { useCreateLocations } from '@/queries/locations';

interface LocationFormProps {
	open: boolean;
	onClose: () => void;
	level?: Level;
	parentId?: string;
	siteId?: string;
	onSubmitted?: () => Promise<void> | void;
}

const LocationForm: FunctionComponent<LocationFormProps> = ({
	open,
	onClose,
	level,
	parentId,
	siteId,
	onSubmitted,
}): ReactElement => {
	const { dispatch } = useToastMessage();
	const [locations, setLocations] = useState<string[]>(['']);

	useEffect(() => {
		if (open) setLocations(['']);
	}, [open, setLocations]);

	const disabled = useMemo(
		() => !locations.every((location) => location.length) || !locations.length,
		[locations]
	);
	const levelName = useMemo(() => level?.name || '', [level]);
	const levelNamePlural = useMemo(() => `${levelName}s`, [levelName]);

	const { isLoading: isCreateLocationsSubmitting, mutate: onCreateLocationsSubmit } =
		useCreateLocations({
			onSuccess: async () => {
				await onSubmitted?.();
				dispatch({
					type: 'success',
					content: `${capitalize(
						locations.length > 1 ? levelNamePlural : levelName
					)} successfully created`,
				});
				onClose();
			},
		});

	if (!level || !siteId) {
		return <></>;
	}

	return (
		<FormDialog
			title={`Create new ${level.name}`}
			open={open}
			onClose={onClose}
			actions={{
				confirm: {
					label: `Create ${level.name}`,
					isLoading: isCreateLocationsSubmitting,
					disabled,
				},
				cancel: { label: 'Cancel' },
			}}
			onSubmit={() =>
				onCreateLocationsSubmit({
					id: siteId,
					request: { request: { parentId, names: locations }, formKey: LOCATIONS_FORM_KEY },
				})
			}
			maxWidth="xs"
			fullWidth
		>
			<Stack direction="column" spacing={2} justifyContent="flex-start">
				{locations.map((location, key) => (
					<RemovableTextField
						name={`locations[${key}]`}
						formKey={LOCATIONS_FORM_KEY}
						key={key}
						label={`${capitalize(level.name)} name`}
						value={location}
						fullWidth
						onRemove={() => setLocations(locations.filter((_, index) => index !== key))}
						onChange={(event: ChangeEvent<HTMLInputElement>) =>
							setLocations(
								locations.map((value, index) => (index !== key ? value : event.target.value))
							)
						}
					/>
				))}
				<Stack direction="row" justifyContent="start">
					<Button
						variant="text"
						startIcon={<AddRounded />}
						onClick={() => setLocations([...locations, ''])}
					>
						Add more
					</Button>
				</Stack>
			</Stack>
		</FormDialog>
	);
};

export default LocationForm;
